import { Document, Image, Page, Text, View } from '@react-pdf/renderer'

import booksPNG from '/assets/report/books.png'
import diagramPNG from '/assets/report/diagram.png'
import diagramClockPNG from '/assets/report/diagram-clock.png'
import diagramNeedlePNG from '/assets/report/diagram-needle.png'

import {
  SurveyReportScoringRanges,
  SurveyTotalReport
} from '../../../../api/services/user_assignment_codes/types.ts'
import { PdfCircle } from '../../../pdf/PdfCircle.tsx'
import {
  getAveragePercentagePatients,
  getScoreRangeIndex
} from '../handlers.ts'
import Signals, { SIGNALS } from '../Signals'
import { PDF_CONTENT } from './pdfContent.ts'
import { surveyReportPDFStyle } from './style.ts'

type Props = {
  data: SurveyTotalReport | null | undefined
  scoringRangeImages: Record<number, string>
}

const SurveyReportPDF_1 = (props: Props) => {
  const styles = surveyReportPDFStyle()
  const pdf = PDF_CONTENT

  const index = getScoreRangeIndex(
    props.data?.scoring_ranges as SurveyReportScoringRanges[],
    Math.trunc(Number(props.data?.average_score) as number)
  )

  const avaregaPatients = getAveragePercentagePatients(
    props.data?.scoring_ranges as SurveyReportScoringRanges[],
    props.data?.total_patients_count as number
  )

  const chunkArray = <T,>(array: T[], size: number): T[][] => {
    const result: T[][] = []

    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size))
    }

    return result
  }

  const rows = chunkArray(props.data?.conclusions ?? [], 2)

  const needleRotateAdjust = 45
  const rotateZero = -180 + needleRotateAdjust
  const max = props.data?.scoring_ranges[2].max_score ?? 1
  const avr = props.data?.average_score ?? 1
  const rotate = rotateZero + (avr / max) * 180

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={[styles.headerTitle, pdf.headerTitle.style ?? {}]}>
          {pdf.headerTitle.text}
        </Text>
        <Text style={[styles.title, pdf.title.style ?? {}]}>
          {pdf.title.text}
        </Text>

        <View style={styles.imageWrapper}>
          <Image style={styles.diagram} src={diagramPNG} />
        </View>

        <Text style={[styles.text, pdf.paragraf_1_1.style ?? {}]}>
          {pdf.paragraf_1_1.text}
        </Text>
        <Text style={[styles.text, pdf.paragraf_1_2.style ?? {}]}>
          {pdf.paragraf_1_2.text}
        </Text>
        <Text style={[styles.text, pdf.paragraf_1_3.style ?? {}]}>
          {pdf.paragraf_1_3.text}
        </Text>
        <Text style={[styles.text, pdf.paragraf_1_4.style ?? {}]}>
          {pdf.paragraf_1_4.text}
        </Text>
        <Text style={[styles.text, pdf.paragraf_1_5.style ?? {}]}>
          {pdf.paragraf_1_5.text}
        </Text>

        {/*Aktualny wynik raportu*/}
        <View style={styles.section} break>
          <Text style={[styles.subtitle, styles.center]}>
            {pdf.wynik_subtitle_1.text}
          </Text>
          <Text style={styles.text}>{pdf.p_wynik_2_1.text}</Text>

          <View style={{ flexDirection: 'row' }}>
            <View style={{ alignItems: 'center', justifyContent: 'center' }}>
              <View
                style={{
                  position: 'relative',
                  width: 150,
                  height: 150
                }}
              >
                <Image style={styles.smallDiagram} src={diagramClockPNG} />
                <Image
                  style={[
                    styles.smallDiagram,
                    { transform: `rotate(${rotate}deg)` }
                  ]}
                  src={diagramNeedlePNG}
                />
                <Text style={styles.textDiagram_1}>{avr}</Text>
                <Text style={styles.textDiagram_2}>/ {max} pkt</Text>
              </View>
            </View>

            <View style={{ flex: 1, flexDirection: 'column' }}>
              <Text
                style={[
                  styles.text,
                  pdf.p_wynik_2_2_1.style ?? {},
                  { flex: 1 }
                ]}
              >
                {pdf.p_wynik_2_2_1.text}{' '}
                <Text
                  style={[styles.text, { fontSize: 16, fontWeight: 'bold' }]}
                >
                  {props.data?.average_score.toFixed(1)}
                  {' / '}
                  {props.data?.scoring_ranges[2].max_score}
                  {' pkt'}
                </Text>
              </Text>

              <Text style={[styles.text, { flex: 1 }]}>
                {pdf.p_wynik_2_2_2.text}{' '}
                <Text
                  style={[styles.text, { fontSize: 16, fontWeight: 'bold' }]}
                >
                  {(avaregaPatients[0] + avaregaPatients[1]).toFixed(1)}
                  {'%'}
                </Text>{' '}
                {pdf.p_wynik_2_2_3.text}
              </Text>
            </View>
          </View>

          <Text style={[styles.text, pdf.p_wynik_2_3.style ?? {}]}>
            {pdf.p_wynik_2_3.text}
          </Text>
          <View style={styles.listItem}>
            {/*<PdfCircle type={'FA-SLD'} size={4} />*/}

            <Text style={{ paddingLeft: 20 }}>
              <PdfCircle type={'FA-SLD'} size={14} color={SIGNALS[2].color} />
              {avaregaPatients[0].toFixed(1)} %
            </Text>
            <Text style={[styles.text, pdf.li_wynik_2_3_1.style ?? {}]}>
              {pdf.li_wynik_2_3_1.text}
            </Text>
          </View>
          <View style={styles.listItem}>
            {/*<PdfCircle type={'FA-SLD'} size={4} />*/}
            <Text style={{ paddingLeft: 20 }}>
              <PdfCircle type={'FA-SLD'} size={14} color={SIGNALS[1].color} />
              {avaregaPatients[1].toFixed(1)} %
            </Text>
            <Text style={[styles.text, pdf.li_wynik_2_3_2.style ?? {}]}>
              {pdf.li_wynik_2_3_2.text}
            </Text>
          </View>
          <View style={styles.listItem}>
            {/*<PdfCircle type={'FA-SLD'} size={4} />*/}
            <Text style={{ paddingLeft: 20 }}>
              <PdfCircle type={'FA-SLD'} size={14} color={SIGNALS[0].color} />
              {avaregaPatients[2].toFixed(1)} %
            </Text>
            <Text style={[styles.text, pdf.li_wynik_2_3_3.style ?? {}]}>
              {pdf.li_wynik_2_3_3.text}
            </Text>
          </View>
        </View>

        {/*Strefy*/}
        <View style={styles.section}>
          <Text style={[styles.text, pdf.strefa_1.style ?? {}]}>
            <PdfCircle type={'FA-SLD'} size={14} color={SIGNALS[2].color} />
            {pdf.strefa_1.text}
            {`(${props.data?.scoring_ranges[0].min_score}–${props.data?.scoring_ranges[0].max_score} pkt/${max})`}
          </Text>
          <Text style={[styles.text, pdf.p_strefa_1_1.style ?? {}]}>
            {pdf.p_strefa_1_1.text}
          </Text>
        </View>

        <View style={[styles.section, { paddingVertical: 0 }]}>
          <Text style={[styles.text, pdf.strefa_2.style ?? {}]}>
            <PdfCircle type={'FA-SLD'} size={14} color={SIGNALS[1].color} />
            {pdf.strefa_2.text}
            {`(${props.data?.scoring_ranges[1].min_score}–${props.data?.scoring_ranges[1].max_score} pkt/${max})`}
          </Text>
          <Text style={[styles.text, pdf.p_strefa_2_1.style ?? {}]}>
            {pdf.p_strefa_2_1.text}
          </Text>
        </View>

        <View style={styles.section}>
          <Text style={[styles.text, pdf.strefa_3.style ?? {}]}>
            <PdfCircle type={'FA-SLD'} size={14} color={SIGNALS[0].color} />
            {pdf.strefa_3.text}
            {`(${props.data?.scoring_ranges[2].min_score}–${props.data?.scoring_ranges[2].max_score} pkt/${max})`}
          </Text>
          <Text style={[styles.text, pdf.p_strefa_3_1.style ?? {}]}>
            {pdf.p_strefa_3_1.text}
          </Text>
        </View>

        {/* Sygnaly */}
        <View style={styles.signals}>
          <Text
            style={[styles.subtitle, pdf.signals.style ?? {}, styles.center]}
          >
            {pdf.signals.text}
          </Text>
          <Text style={[styles.text, pdf.signals_1.style ?? {}, styles.center]}>
            {pdf.signals_1.text}
          </Text>

          {rows.map((row, rowIndex) => (
            <View key={rowIndex} style={styles.signalsRow} wrap={false}>
              {row.map((item, itemIndex) => (
                <Signals
                  key={itemIndex}
                  conclusion={item}
                  total={props.data?.total_patients_count}
                />
              ))}
            </View>
          ))}
        </View>

        {/*Podsumowanie*/}
        <View style={styles.section} break>
          <Text style={[styles.subtitle, pdf.summary.style ?? {}]}>
            {pdf.summary.text}
          </Text>

          {index === 2 && (
            <>
              <Text style={[styles.text, pdf.p_summary_1_1.style ?? {}]}>
                {pdf.p_summary_1_1.text}
              </Text>
              <Text style={[styles.text, pdf.p_summary_1_2.style ?? {}]}>
                {pdf.p_summary_1_2.text}
              </Text>

              <View style={styles.listItem}>
                <PdfCircle type={'FA-SLD'} size={4} />
                <Text style={[styles.text, pdf.li_summary_1_2_1.style ?? {}]}>
                  {pdf.li_summary_1_2_1.text}
                </Text>
              </View>

              <View style={styles.listItem}>
                <PdfCircle type={'FA-SLD'} size={4} />
                <Text style={[styles.text, pdf.li_summary_1_2_2.style ?? {}]}>
                  {pdf.li_summary_1_2_2.text}
                </Text>
              </View>

              <View style={styles.listItem}>
                <PdfCircle type={'FA-SLD'} size={4} />
                <Text style={[styles.text, pdf.li_summary_1_2_3.style ?? {}]}>
                  {pdf.li_summary_1_2_3.text}
                </Text>
              </View>

              <View style={styles.listItem}>
                <PdfCircle type={'FA-SLD'} size={4} />
                <Text style={[styles.text, pdf.li_summary_1_2_4.style ?? {}]}>
                  {pdf.li_summary_1_2_4.text}
                </Text>
              </View>

              <Text style={[styles.text, pdf.p_summary_1_3.style ?? {}]}>
                {pdf.p_summary_1_3.text}
              </Text>
              <Text style={[styles.text, pdf.p_summary_1_4.style ?? {}]}>
                {pdf.p_summary_1_4.text}
              </Text>
            </>
          )}

          {index === 1 && (
            <>
              <Text style={[styles.text, pdf.p_summary_2_1.style ?? {}]}>
                {pdf.p_summary_2_1.text}
              </Text>
              <Text style={[styles.text, pdf.p_summary_2_2.style ?? {}]}>
                {pdf.p_summary_2_2.text}
              </Text>
              <Text style={[styles.text, pdf.p_summary_2_3.style ?? {}]}>
                {pdf.p_summary_2_3.text}
              </Text>
            </>
          )}

          {index === 0 && (
            <>
              <Text style={[styles.text, pdf.p_summary_3_1.style ?? {}]}>
                {pdf.p_summary_3_1.text}
              </Text>
              <Text style={[styles.text, pdf.p_summary_3_2.style ?? {}]}>
                {pdf.p_summary_3_2.text}
              </Text>

              <View style={styles.listItem}>
                <PdfCircle type={'FA-SLD'} size={4} />
                <Text style={[styles.text, pdf.li_summary_3_2_1.style ?? {}]}>
                  {pdf.li_summary_3_2_1.text}
                </Text>
              </View>
              <View style={styles.listItem}>
                <PdfCircle type={'FA-SLD'} size={4} />
                <Text style={[styles.text, pdf.li_summary_3_2_2.style ?? {}]}>
                  {pdf.li_summary_3_2_2.text}
                </Text>
              </View>
              <View style={styles.listItem}>
                <PdfCircle type={'FA-SLD'} size={4} />
                <Text style={[styles.text, pdf.li_summary_3_2_3.style ?? {}]}>
                  {pdf.li_summary_3_2_3.text}
                </Text>
              </View>

              <Text style={[styles.text, pdf.p_summary_3_3.style ?? {}]}>
                {pdf.p_summary_3_3.text}
              </Text>
            </>
          )}

          <View style={styles.imageWrapper}>
            {props.data?.scoring_ranges[index].scoring_range_image && (
              <Image
                style={styles.image}
                src={props.scoringRangeImages[index]}
              />
            )}
          </View>
        </View>

        {/* end */}
        <View style={styles.section} break>
          <Text style={[styles.subtitle, pdf.p_end_1.style ?? {}]}>
            {pdf.p_end_1.text}
          </Text>

          <Text style={[styles.text, pdf.p_end_1_1.style ?? {}]}>
            {pdf.p_end_1_1.text}
          </Text>
          <Text style={[styles.text, pdf.p_end_1_2.style ?? {}]}>
            {pdf.p_end_1_2.text}
          </Text>
          <Text style={[styles.text, pdf.p_end_1_3.style ?? {}]}>
            {pdf.p_end_1_3.text}
          </Text>
          <Text style={[styles.text, pdf.p_end_1_4.style ?? {}]}>
            {pdf.p_end_1_4.text}
          </Text>
          <Text style={[styles.text, pdf.p_end_1_5.style ?? {}]}>
            {pdf.p_end_1_5.text}
          </Text>
          <Text style={[styles.text, pdf.p_end_1_6.style ?? {}]}>
            {pdf.p_end_1_6.text}
          </Text>

          <View
            style={{
              flex: 1,
              marginTop: 80,
              flexDirection: 'row',
              alignItems: 'center'
            }}
          >
            <Text style={[styles.text, pdf.p_end_2.style ?? {}]}>
              {pdf.p_end_2.text}
            </Text>
            <Image
              style={[styles.image, { width: 200, height: 200 }]}
              src={booksPNG}
            />
          </View>
        </View>

        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  )
}

export default SurveyReportPDF_1
